import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from '@emotion/styled';
import { 
  getRecords, 
  getColumns, 
  getAllLoans,
  setSelectedLoanId,
  setSelectedColumn,
  setCombinedData,
  setSelectedLoans,
  setSelectedBorrowers,
  setShowSystemValues,
  clearErrors
} from '../store/slices/databaseSlice';
import { getOverridesForLoan } from '../store/slices/overridesSlice';
import { addNotification } from '../store/slices/uiSlice';
import { Check, Eye, EyeOff, Columns, MessageSquare, Edit, Lock, Unlock, UserCheck, UserX, Circle, FileX, ServerOff } from 'lucide-react';
import { colors } from '../theme/colors';
import { getFieldBackgroundColorCentralized, getFieldTextColor, getMatchStatusFromRow, getDisplayMatchStatus, getDisplayStatusColors } from '../services/colorService';
import MatchStatusIcon from '../components/MatchStatusIcon';
import { apiFetch } from '../utils/apiUtils';
import api from '../services/api';
import ColumnVisibilityModal from '../components/ColumnVisibilityModal';
import CommentsModal from '../components/CommentsModal';
import LoadingSpinner from '../components/LoadingSpinner';
import PageContainer from '../components/PageContainer';
import DataTable from '../components/Table';
import FieldModal from '../components/FieldModal';
import SearchPresets from '../components/SearchPresets';
import CollapsibleLegend from '../components/CollapsibleLegend';
import { useAuth } from '../contexts/AuthContext';


// Removed PageContainer - now using reusable PageContainer component from components/PageContainer.jsx

const Title = styled.h2`
  margin: 0;
  color: #2c3e50;
  font-size: 1.8rem;
`;

const ControlsSection = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  align-items: end;
`;

const ControlGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #2c3e50;
`;

// Adobe palette tool style buttons
const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  color: #495057;
  cursor: pointer;
  transition: all 0.15s;
  
  &:hover {
    background: #e9ecef;
    border-color: #dee2e6;
  }
  
  &.active {
    background: #2c2c2c;
    border-color: #2c2c2c;
    color: white;
  }
`;

const ToggleButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.5rem 0.75rem;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  color: #495057;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 500;
  transition: all 0.15s;
  
  &:hover {
    background: #e9ecef;
    border-color: #dee2e6;
  }
  
  &.active {
    background: #28a745;
    border-color: #28a745;
    color: white;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Divider = styled.div`
  width: 2px;
  height: 24px;
  background: #dee2e6;
  margin: 0 0.5rem;
  border-radius: 1px;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
`;

const Select = styled.select`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  min-width: 200px;
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background-color: #3b82f6;
  color: white;
  
  &:hover {
    background-color: #2563eb;
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;


const StatusMessage = styled.div`
  padding: 1rem;
  margin: 1rem 0;
  border-radius: 4px;
  font-weight: 500;
  
  ${props => {
    if (props.type === 'success') {
      return 'background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb;';
    }
    if (props.type === 'error') {
      return 'background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb;';
    }
    return 'background-color: #d1ecf1; color: #0c5460; border: 1px solid #bee5eb;';
  }}
`;

const ErrorMessage = styled.div`
  background-color: #f8d7da;
  color: #721c24;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
  border: 1px solid #f5c6cb;
`;

const NoDataMessage = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
  font-style: italic;
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
  padding: 1rem 0;
`;

const PaginationInfo = styled.div`
  color: #6c757d;
  font-size: 0.875rem;
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  color: #333;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #f8f9fa;
    border-color: #3b82f6;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &.active {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }
`;

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem 1rem;
  background: #f8f9fa;
  border-radius: 6px;
  border: 1px solid #e9ecef;
`;

const LegendTitle = styled.span`
  font-weight: 600;
  color: #495057;
  font-size: 0.875rem;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #6c757d;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  background: ${props => props.color};
`;



function DatabaseViewer() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    records,
    columns,
    allLoans,
    selectedLoanId,
    selectedColumn,
    loading,
    errors,
    status
  } = useSelector(state => state.database);

  // Get overrides for all loans
  const { overrides } = useSelector(state => state.overrides);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(25);
  const [totalRecords, setTotalRecords] = useState(0);
  const [showColumnModal, setShowColumnModal] = useState(false);
  const [showCommentsModal, setShowCommentsModal] = useState(false);
  const [selectedLoanForComments, setSelectedLoanForComments] = useState(null);
  // Get showSystemValues from Redux store
  const showSystemValues = useSelector(state => state.database.showSystemValues || false);
  const [activeFields, setActiveFields] = useState([]);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    // Load from localStorage or default to empty object (all visible)
    try {
      const saved = localStorage.getItem('caseListVisibleColumns');
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });
  const [expandedRows, setExpandedRows] = useState(new Set());
  
  // Expose setShowColumnModal and visibleColumns to ActionMenu via window
  useEffect(() => {
    window.caseListSetShowColumnModal = setShowColumnModal;
    window.caseListVisibleColumns = visibleColumns;
    window.caseListActiveFields = activeFields;
    return () => {
      delete window.caseListSetShowColumnModal;
      delete window.caseListVisibleColumns;
      delete window.caseListActiveFields;
    };
  }, [visibleColumns, activeFields]);
  
  // New state for unified loans
  const [unifiedLoans, setUnifiedLoans] = useState([]);
  const [unifiedLoansLoading, setUnifiedLoansLoading] = useState(false);
  const [combinedData, setLocalCombinedData] = useState([]);
  
  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  
  // Get advanced filters from Redux
  const advancedFilters = useSelector(state => state.database.advancedFilters || []);
  
  // FieldModal state
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [selectedField, setSelectedField] = useState(null);
  const [selectedLoanForField, setSelectedLoanForField] = useState(null);
  const [fieldHistory, setFieldHistory] = useState([]);

  // Load active fields from API
  const loadActiveFields = async () => {
    try {
      const response = await apiFetch('/system-files/fields/stats');
      // apiFetch already returns parsed JSON
      const data = response;
      if (data.success) {
        // Store full field objects with UUIDs, not just names
        const activeFieldObjects = data.fields
          .filter(field => field.status === 'active');
        setActiveFields(activeFieldObjects);
      }
    } catch (error) {
      console.error('Error loading active fields:', error);
    }
  };

  // Load all loans from admin API
  const loadUnifiedLoans = async () => {
    setUnifiedLoansLoading(true);
    try {
      // Use api service which automatically includes auth token
      const response = await api.get('/admin/loans/unified');
      const data = response.data;
      if (data.success) {
        setUnifiedLoans(data.loans);
        
        // Note: Removed loadOverridesForAllLoans call to prevent excessive API requests
        // Overrides will be loaded on-demand when individual loans are selected
      }
    } catch (error) {
      console.error('Error loading unified loans:', error);
    } finally {
      setUnifiedLoansLoading(false);
    }
  };

  // Note: Removed loadOverridesForAllLoans function to prevent excessive API requests
  // Overrides are now loaded on-demand when individual loans are selected

  // Combine case list data with unified loans data
  const combineData = () => {
    // Need at least unifiedLoans for the typeahead to work
    if (!unifiedLoans.length) return;
    
    const combined = [];
    
    // Create a map of existing case list data by loan_id
    const caseDataMap = {};
    records.forEach(record => {
      const loanId = record.loan_id || record['Loan ID'] || record['Loan_ID'];
      if (loanId) {
        caseDataMap[loanId] = record;
      }
    });

    // Create a map of overrides by loan_id and field_name
    const overridesMap = {};
    overrides.forEach(override => {
      if (!overridesMap[override.loan_id]) {
        overridesMap[override.loan_id] = {};
      }
      overridesMap[override.loan_id][override.field_name] = override;
    });

    // Process all loans
    unifiedLoans.forEach(loan => {
      const caseData = caseDataMap[loan.loan_id];
      
      
      if (caseData) {
        // Loan has case data - add it to the top
        // Clean the data to avoid rendering objects
        const cleanCaseData = { ...caseData };
        delete cleanCaseData._loanInfo; // Remove any existing _loanInfo
        
        // Add override information
        const loanOverrides = overridesMap[loan.loan_id] || {};
        const matchStatusOverrides = Object.values(loanOverrides)
          .filter(override => override.override_type === 'match_status')
          .map(override => ({
            field_name: override.field_name,
            match_status: override.overridden_value,
            created_by: override.created_by
          }));

        // Preserve assignment information from unified loans
        combined.push({
          ...cleanCaseData,
          _hasData: true,
          assigned_to_name: loan.assigned_to_name,
          assigned_by_name: loan.assigned_by_name,
          assigned_at: loan.assigned_at,
          assignment_status: loan.assignment_status,
          _matchStatusOverrides: matchStatusOverrides
        });
      } else {
        // Loan doesn't have case data - add it to the bottom
        const emptyRecord = {
          loan_id: loan.loan_id,
          _hasData: false,
          assigned_to_name: loan.assigned_to_name,
          assigned_by_name: loan.assigned_by_name,
          assigned_at: loan.assigned_at,
          assignment_status: loan.assignment_status
        };
        
        // Add empty fields to match case data structure
        if (records.length > 0) {
          Object.keys(records[0]).forEach(key => {
            if (key !== 'loan_id' && key !== 'Loan ID' && key !== 'Loan_ID' && !key.startsWith('_')) {
              emptyRecord[key] = 'N/A';
            }
          });
        }
        
        combined.push(emptyRecord);
      }
    });

    // Sort: loans with data first, then loans without data
    combined.sort((a, b) => {
      if (a._hasData && !b._hasData) return -1;
      if (!a._hasData && b._hasData) return 1;
      return 0;
    });

    setLocalCombinedData(combined);
  };

  // Get current user from AuthContext (not Redux)
  const { user: authUser } = useAuth();
  const currentUser = authUser; // Use authenticated user for filtering

  // Combine data whenever records or unifiedLoans change
  useEffect(() => {
    combineData();
  }, [records, unifiedLoans, overrides]);

  // Update Redux store with data - ensure serializable data only
  useEffect(() => {
    // Deep clone to ensure serializability and prevent reference issues
    try {
      const serializableData = JSON.parse(JSON.stringify(combinedData));
      dispatch(setCombinedData(serializableData));
    } catch (error) {
      console.error('Error serializing combinedData:', error);
    }
  }, [combinedData, dispatch]);

  // Load columns and initial data on component mount
  useEffect(() => {
    dispatch(getColumns());
    loadActiveFields(); // Load active fields first
    loadUnifiedLoans(); // Load unified loans
    // Load initial page of records - use getRecords to show all columns
    loadPage(1);
  }, [dispatch]);



  // Load a specific page of records
  const loadPage = (page) => {
    setCurrentPage(page);
    // Use getRecords with 'All Fields' to get all columns and their values
    dispatch(getRecords({ loanId: '', column: 'All Fields' }));
  };

  // Get selected loans from Redux store for filtering
  const reduxSelectedLoans = useSelector(state => state.database.selectedLoans || []);
  const reduxSelectedBorrowers = useSelector(state => state.database.selectedBorrowers || []);
  const reduxRecords = useSelector(state => state.database.records || []);

  // Filter the combined data based on status filter and selected loans
  const filteredData = React.useMemo(() => {
    if (!combinedData.length) return [];
    
    let filtered = combinedData;
    
    // Apply selected loans filter first (if any loans are selected)
    if (reduxSelectedLoans.length > 0) {
      filtered = filtered.filter(loan => reduxSelectedLoans.includes(loan.loan_id));
      
      // If we have selected loans but no matches in combinedData, 
      // these loans might not have OCR'd records - show them anyway
      if (filtered.length === 0 && reduxSelectedLoans.length > 0) {
        const missingLoans = reduxSelectedLoans.filter(loanId => 
          !combinedData.some(loan => loan.loan_id === loanId)
        );
        
        // Add missing loans from unifiedLoans (these don't have OCR'd records)
        const missingLoanData = unifiedLoans
          .filter(loan => missingLoans.includes(loan.loan_id))
          .map(loan => ({
            loan_id: loan.loan_id,
            'Borrower Name': loan.borrower_name || 'N/A',
            borrower_name: loan.borrower_name || 'N/A',
            _hasData: false, // Mark as no OCR data
            _isSystemOnly: true // Mark as system data only
          }));
        
        filtered = [...filtered, ...missingLoanData];
      }
    }
    
    // Apply selected borrowers filter (if any borrowers are selected)
    if (reduxSelectedBorrowers.length > 0) {
      // Find loan IDs associated with the selected borrowers from unifiedLoans
      const borrowerLoanIds = [];
      reduxSelectedBorrowers.forEach(borrower => {
        let borrowerName = typeof borrower === 'string' ? borrower : borrower.borrower_name;
        // Strip the loan count suffix from the borrower name (e.g., " (1 loans)" -> "")
        borrowerName = borrowerName.replace(/\s*\(\d+\s+loans?\)\s*$/, '');
        
        // Find loans in unifiedLoans that match this borrower
        const matchingLoans = unifiedLoans.filter(loan => 
          loan.borrower_name === borrowerName
        );
        
        borrowerLoanIds.push(...matchingLoans.map(loan => loan.loan_id));
      });
      
      // Filter by the loan IDs found from borrower selection
      if (borrowerLoanIds.length > 0) {
        filtered = filtered.filter(loan => borrowerLoanIds.includes(loan.loan_id));
      } else {
        filtered = [];
      }
    }
    
    // Apply status filter
    if (statusFilter === 'my_cases') {
      // Use authenticated user for filtering
      filtered = filtered.filter(loan => 
        loan.assigned_to_name === currentUser?.full_name
      );
    } else if (statusFilter === 'has_scans') {
      filtered = filtered.filter(loan => loan._hasData);
    } else if (statusFilter === 'without_data') {
      filtered = filtered.filter(loan => !loan._hasData);
    }
    // 'all' shows everything, no additional filtering needed
    
    // Apply advanced filters (field value filtering + match status) - apply ALL filters (AND logic)
    if (advancedFilters.length > 0) {
      filtered = filtered.filter(loan => {
        // Loan must match ALL active filters
        return advancedFilters.every(filter => {
          let matchesValue = true;
          let matchesStatus = true;
          
          // Check value filter (if values are specified)
          if (filter.values && filter.values.length > 0) {
            const fieldValue = loan[filter.fieldName];
            matchesValue = filter.values.includes(fieldValue);
          }
          
          // Check match status filter (if statuses are specified)
          if (filter.matchStatuses && filter.matchStatuses.length > 0) {
            // Get the match status for this field
            const matchStatusKey = `${filter.fieldName}_match`;
            const systemDocumentMatch = loan[matchStatusKey];
            const masterValue = loan[`${filter.fieldName}_master`];
            const fieldValue = loan[filter.fieldName];
            
            // Use the centralized logic to determine display status
            const fieldData = {
              value: fieldValue,
              master_value: masterValue,
              system_document_match: systemDocumentMatch
            };
            
            // Use the imported getDisplayMatchStatus function
            const displayStatus = getDisplayMatchStatus(fieldData);
            
            matchesStatus = filter.matchStatuses.includes(displayStatus);
          }
          
          // Both conditions must be true (if specified)
          return matchesValue && matchesStatus;
        });
      });
    }
    
    return filtered;
  }, [combinedData, statusFilter, currentUser, reduxSelectedLoans, reduxSelectedBorrowers, unifiedLoans, advancedFilters]);

  // Calculate pagination data from the filtered data
  const totalPages = Math.ceil((filteredData.length || 0) / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const currentPageData = filteredData.slice(startIndex, endIndex);
  

  const handleSearch = () => {
    dispatch(getRecords({ loanId: selectedLoanId, column: selectedColumn }));
  };

  const handleViewLoanDetails = (loanId) => {
    navigate(`/document-analysis/${loanId}`);
  };

  // Load field history for a specific field
  const loadFieldHistory = async (loanId, fieldName) => {
    try {
      const response = await apiFetch(`/overrides/${loanId}/${fieldName}`);
      // apiFetch already returns parsed JSON
      const overrides = response;
      // Convert overrides to history format
      const history = overrides.map(override => ({
        type: 'value_change',
        title: `${override.override_type.replace('_', ' ')} Updated`,
        description: `Field ${override.override_type} was overridden`,
        timestamp: new Date(override.created_at).toLocaleDateString(),
        user: override.created_by,
        changes: {
          [override.override_type]: {
            from: 'Original Value',
            to: override.overridden_value
          }
        }
      }));
      setFieldHistory(history);
    } catch (error) {
      console.error('Error loading field history:', error);
      setFieldHistory([]);
    }
  };

  // Handle field click to open FieldModal
  const handleFieldClick = async (fieldName, fieldValue, loanData, isSystemRow = false) => {
    if (isSystemRow) return; // Don't open modal for system rows
    
    const loanId = loanData.loan_id || loanData['Loan ID'] || loanData['Loan_ID'];
    const borrowerName = loanData.borrower_name || loanData['Borrower Name'] || 'Unknown Borrower';
    
    // Get master value for this field
    const masterKey = `${fieldName}_master`;
    const masterValue = loanData[masterKey] || 'Not Found';
    
    // Get source file for this field
    const sourceKey = `${fieldName}_source`;
    const source = loanData[sourceKey] || 'Unknown';
    
    setSelectedField({
      fieldName,
      currentValues: {
        value: fieldValue,
        master_value: masterValue,
        source: source
      },
      loanId,
      borrowerName
    });
    setSelectedLoanForField(loanData);
    
    // Load field history
    await loadFieldHistory(loanId, fieldName);
    
    setShowFieldModal(true);
  };

  // Handle expand all rows toggle
  const handleExpandAllToggle = () => {
    if (expandedRows.size === currentPageData.length) {
      // All expanded, collapse all
      setExpandedRows(new Set());
    } else {
      // Not all expanded, expand all
      setExpandedRows(new Set(currentPageData.map((_, index) => index)));
    }
  };


  const handleLoanIdChange = (e) => {
    dispatch(setSelectedLoanId(e.target.value));
  };

  const handleColumnChange = (e) => {
    dispatch(setSelectedColumn(e.target.value));
  };

  // Helper function to create data with system values rows
  const getDataWithSystemValues = (data) => {
    const result = [];
    
    data.forEach(loan => {
      // Add the original document values row
      const documentRow = {
        ...loan,
        _rowType: 'document',
        _loanId: loan.loan_id || loan['Loan ID'] || loan['Loan_ID']
      };
      
      // Ensure all values are strings to prevent React rendering errors
      Object.keys(documentRow).forEach(key => {
        if (documentRow[key] !== null && documentRow[key] !== undefined) {
          documentRow[key] = String(documentRow[key]);
        } else {
          documentRow[key] = '';
        }
      });
      
      result.push(documentRow);
      
      // Add the system/master values row
      const systemRow = { _rowType: 'system', _loanId: loan.loan_id || loan['Loan ID'] || loan['Loan_ID'] };
      
      // Copy all fields but use master_value if available
      Object.keys(loan).forEach(key => {
        if (key !== 'loan_id' && key !== 'Loan ID' && key !== 'Loan_ID' && !key.endsWith('_match') && !key.endsWith('_master')) {
          // Look for master value - the database now provides {field}_master columns
          const masterKey = `${key}_master`;
          
          if (loan[masterKey] && loan[masterKey] !== '') {
            // Ensure the value is a string to prevent React rendering errors
            systemRow[key] = String(loan[masterKey]);
          } else {
            // If no master value, show "Not Found"
            systemRow[key] = 'Not Found';
          }
        }
        // Don't copy loan_id for system rows - leave it blank
      });
      
      result.push(systemRow);
    });
    
    return result;
  };


  // Helper function to generate table columns based on data structure
  const getTableColumns = (records) => {
    if (!records.length) return [];

    const firstRecord = records[0];
    const columns = [];

    // Always show loan_id first with status icon
    if (firstRecord.hasOwnProperty('loan_id')) {
      columns.push({
        key: 'loan_id',
        title: 'Loan ID',
        align: 'center',
        sortable: true,
        width: '180px', // Slightly wider to accommodate icon
        render: (value, row) => {
          // Don't show loan ID for system rows
          if (row._rowType === 'system') {
            return (
              <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', backgroundColor: '#f8f9fa' }}>
                <span style={{ color: '#6b7280', fontStyle: 'italic' }}></span>
              </div>
            );
          }

          // Get status information
          const hasData = row._hasData !== false;
          const assignmentStatus = row.assignment_status;
          const dataStatus = row.data_status;
          const matchStatusOverrides = row._matchStatusOverrides || [];

          // Determine status and icon
          let statusIcon, statusColor, tooltipText;

          if (dataStatus === 'archived') {
            statusIcon = Lock;
            statusColor = '#6b7280'; // Gray
            tooltipText = 'Archived';
          } else if (!hasData) {
            statusIcon = FileX;
            statusColor = '#9ca3af'; // Light gray
            tooltipText = 'No Documents';
          } else if (assignmentStatus === 'assigned') {
            statusIcon = UserCheck;
            statusColor = '#10b981'; // Green
            tooltipText = 'Assigned';
          } else if (assignmentStatus === 'unassigned') {
            statusIcon = Unlock;
            statusColor = '#f59e0b'; // Yellow/Orange
            tooltipText = 'Open';
          } else {
            statusIcon = Unlock;
            statusColor = '#f59e0b'; // Default to open
            tooltipText = 'Open';
          }

          const StatusIcon = statusIcon;
          
          // Add match status override indicator if present
          const overrideIndicator = matchStatusOverrides.length > 0 ? (
            <div 
              style={{ 
                position: 'relative',
                display: 'inline-block'
              }}
              title={`${matchStatusOverrides.length} match status override${matchStatusOverrides.length > 1 ? 's' : ''}`}
            >
              <div 
                style={{
                  position: 'absolute',
                  top: '-4px',
                  right: '-4px',
                  width: '8px',
                  height: '8px',
                  backgroundColor: '#22c55e',
                  borderRadius: '50%',
                  border: '1px solid white'
                }}
              />
            </div>
          ) : null;
          
          return (
            <div 
              style={{ 
                padding: '12px 16px', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                gap: '0.5rem',
                width: '100%' 
              }}
              title={`${value} - ${tooltipText}${matchStatusOverrides.length > 0 ? ` (${matchStatusOverrides.length} match override${matchStatusOverrides.length > 1 ? 's' : ''})` : ''}`}
            >
              <div style={{ position: 'relative' }}>
                <StatusIcon size={16} color={statusColor} />
                {overrideIndicator}
              </div>
              <span>{value}</span>
            </div>
          );
        }
      });
    }

    // Add Actions column second
    columns.push({
      key: 'actions',
      title: 'Actions',
      align: 'center',
      sortable: false,
        width: '140px',
      render: (_, row) => {
        const loanId = row['loan_id'] || row['Loan ID'] || row['Loan_ID'] || 
                      Object.values(row).find(value => 
                        typeof value === 'string' && /^\d{10,}$/.test(value)
                      );
        
        // Don't show action buttons for system rows
        if (row._rowType === 'system') {
          return (
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', backgroundColor: '#f8f9fa' }}>
              <span style={{ color: '#6b7280', fontStyle: 'italic' }}>System</span>
            </div>
          );
        }
        
        if (loanId) {
          // Check if this loan has data or not
          const hasData = row._hasData !== false;
          
          return (
            <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', width: '100%' }}>
              {hasData ? (
                <>
                  <ActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/document-analysis/${loanId}`);
                    }}
                    title="View Details"
                    style={{ background: '#374151', border: '1px solid #374151', color: 'white' }}
                    data-cy={`view-loan-${loanId}`}
                  >
                    <Eye size={16} />
                  </ActionButton>
                  <ActionButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedLoanForComments(loanId);
                      setShowCommentsModal(true);
                    }}
                    title="Add Comment"
                    style={{ background: '#374151', border: '1px solid #374151', color: 'white' }}
                  >
                    <MessageSquare size={16} />
                  </ActionButton>
                </>
              ) : (
                <ActionButton
                  onClick={(e) => {
                    e.stopPropagation();
                    // Navigate to document analysis page for this loan
                    navigate(`/document-analysis/${loanId}`);
                  }}
                  title="Add Documents"
                  style={{ background: '#3a3a3a', border: '1px solid #4a4a4a', color: '#e0e0e0' }}
                >
                  <Edit size={16} />
                </ActionButton>
              )}
            </div>
          );
        }
        return (
          <div style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <span>-</span>
          </div>
        );
      }
    });

    // Add other columns dynamically - show ALL active fields, even if they don't have data
    // First, iterate through all active fields to ensure they're all represented
    activeFields.forEach(field => {
      const fieldName = typeof field === 'string' ? field : field.name;
      const fieldUuid = typeof field === 'string' ? fieldName : (field.field_uuid || field.uuid || fieldName);
      
      // Check if column should be visible (default to true if not set)
      // Hide if EITHER the UUID or fieldName is explicitly set to false
      if (visibleColumns[fieldUuid] === false || visibleColumns[fieldName] === false) return;
      
      // Check if this field exists in the data or if we need to create it
      const hasData = firstRecord.hasOwnProperty(fieldName);
      
      // Use centralized color service for consistent coloring

      let columnConfig = {
        key: fieldUuid, // Use UUID as key for uniqueness
        title: fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
        sortable: true,
        width: (() => {
          // Make borrower name columns wider
          const isBorrowerName = fieldName.toLowerCase().includes('borrower') || fieldName.toLowerCase().includes('name');
          return isBorrowerName ? '200px' : '150px';
        })(),
        getBackgroundColor: (value, row) => {
          // Style system rows differently
          const isSystemRow = row._rowType === 'system';
          
          if (isSystemRow) {
            return '#f8f9fa';
          }
          
          // Use centralized color service with Grid View logic for consistent coloring
          return getFieldBackgroundColorCentralized(row, fieldName);
        },
        render: (value, row) => {
          // For document rows, always show document values (even if empty)
          // For system rows, show master values
          const isSystemRow = row._rowType === 'system';
          const displayValue = isSystemRow 
            ? (row[fieldName] || 'Not Found')  // System row shows master values
            : (row[fieldName] || '');          // Document row shows document values (even if empty)
          
          // Use centralized color service for consistent coloring
          const matchStatus = getMatchStatusFromRow(row, fieldName);
          
          // Build field data for display status (same logic as background color)
          const fieldData = {
            field_name: fieldName,
            value: row[fieldName],
            master_value: row[`${fieldName}_master`],
            system_document_match: row[`${fieldName}_match`]
          };
          
          // Use display status to get text color (same logic as background color)
          const displayStatus = getDisplayMatchStatus(fieldData);
          const displayColors = getDisplayStatusColors(displayStatus);
          const textColor = displayColors.textColor;
          
          const contentStyle = {
            display: 'flex',
            alignItems: 'center',
            minHeight: '40px',
            padding: '12px 16px',
            width: '100%'
          };
          
          // Check if core system data is missing (for document rows only)
          const masterValue = row[`${fieldName}_master`];
          const isCoreDataMissing = !isSystemRow && (masterValue === 'Not Found' || masterValue === 'N/A' || !masterValue);

          return (
            <div style={contentStyle}>
              <span 
                style={{ 
                  flex: 1, 
                  color: isSystemRow ? '#6b7280' : textColor, 
                  fontWeight: isSystemRow ? '400' : (displayStatus === 'no_data' ? '400' : '600'), // Normal weight for no_data
                  fontStyle: isSystemRow ? 'italic' : 'normal',
                  cursor: 'default',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
                title={isSystemRow ? 'System Field' : `${fieldName} (${matchStatus})${isCoreDataMissing ? ' - Core system data missing' : ''}`}
              >
                {/* Show server-off icon if core system data is missing */}
                {isCoreDataMissing && (
                  <ServerOff 
                    size={14} 
                    style={{ 
                      color: '#000000', 
                      flexShrink: 0,
                      opacity: 0.7
                    }} 
                  />
                )}
                
                {/* Show the actual value or FileX icon */}
                {displayValue || (row._hasData ? '' : (
                  <FileX 
                    size={16} 
                    style={{ 
                      color: '#9ca3af', 
                      opacity: 0.7 
                    }} 
                  />
                ))}
              </span>
            </div>
          );
        }
      };
      
      columns.push(columnConfig);
    });
    
    // Also add any non-active fields that exist in the data (for backward compatibility)
    Object.keys(firstRecord).forEach(key => {
      if (key !== 'loan_id' && !key.endsWith('_match') && !key.endsWith('_master') && key !== 'actions') {
        // Skip if we already added this field above
        if (activeFields.includes(key)) return;
        // Skip non-active fields for now - we only want to show active fields by default
        // This maintains backward compatibility but prioritizes active fields
      }
    });

    return columns;
  };

  // Clear errors and filters when component unmounts or when navigating away
  useEffect(() => {
    // Clear filters when mounting (fresh start)
    dispatch(setSelectedLoans([]));
    dispatch(setSelectedBorrowers([]));
    
    return () => {
      dispatch(clearErrors());
      // Clear filters when unmounting to prevent stale state
      dispatch(setSelectedLoans([]));
      dispatch(setSelectedBorrowers([]));
    };
  }, [dispatch]);


  return (
    <PageContainer>
      <CollapsibleLegend />
      <div style={{ padding: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
          <Title>Case List</Title>
        </div>
        
        {/* Status Filter Tabs - Using Reusable SearchPresets Component */}
        <div style={{ marginBottom: '0.75rem' }}>
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <SearchPresets
              presets={[
                { key: 'all', label: 'All Cases', count: combinedData.length },
                { key: 'my_cases', label: 'My Cases', count: combinedData.filter(loan => 
                  loan.assigned_to_name === currentUser?.full_name
                ).length },
                { key: 'has_scans', label: 'Has Scans', count: combinedData.filter(loan => loan._hasData).length },
                { key: 'client_review', label: 'Client Review', count: 0 }
              ]}
              activePreset={statusFilter}
              onPresetChange={setStatusFilter}
            />
            
            {/* More button on same row */}
            <button
              onClick={() => setShowMoreFilters(!showMoreFilters)}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                background: 'white',
                color: '#6b7280',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = '#f9fafb';
                e.target.style.borderColor = '#9ca3af';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'white';
                e.target.style.borderColor = '#d1d5db';
              }}
            >
              {showMoreFilters ? 'Less' : 'More'}
            </button>
          </div>
          
          {/* Second row - more filters (shown when expanded) */}
          {showMoreFilters && (
            <div style={{ marginTop: '0.5rem' }}>
              <SearchPresets
                presets={[
                  { key: 'without_data', label: 'Without Data', count: combinedData.filter(loan => !loan._hasData).length },
                  { key: 'closed', label: 'Closed', count: 0 },
                  { key: 'need_attention', label: 'Need Attention', count: 0 },
                  { key: 'client_resolve', label: 'Client Resolve', count: 0 }
                ]}
                activePreset={statusFilter}
                onPresetChange={setStatusFilter}
              />
            </div>
          )}
        </div>
        
        
        {errors.getRecords && (
          <ErrorMessage>Error: {errors.getRecords}</ErrorMessage>
        )}

        {status && (
          <StatusMessage type="success">
            {status}
          </StatusMessage>
        )}

        {(loading.getRecords || unifiedLoansLoading) ? (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <LoadingSpinner size="large" />
            <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading cases...</p>
          </div>
        ) : currentPageData.length > 0 ? (
          <>
            <DataTable
              data={showSystemValues ? getDataWithSystemValues(currentPageData) : currentPageData.map(loan => {
                // Ensure all values are strings to prevent React rendering errors
                const safeLoan = {};
                Object.keys(loan).forEach(key => {
                  if (loan[key] !== null && loan[key] !== undefined) {
                    safeLoan[key] = String(loan[key]);
                  } else {
                    safeLoan[key] = '';
                  }
                });
                return safeLoan;
              })}
              columns={getTableColumns(currentPageData)}
              pageSize={pageSize}
              searchable={false}
              sortable={true}
              pagination={false}
              rowDataCy="case-row"
              data-cy="case-list-table"
              onRowClick={(row) => {
                const loanId = row['loan_id'] || row['Loan ID'] || row['Loan_ID'] || 
                              Object.values(row).find(value => 
                                typeof value === 'string' && /^\d{10,}$/.test(value)
                              );
                if (loanId) {
                  handleViewLoanDetails(loanId);
                }
              }}
              getRowStyle={(row) => {
                // Different styles for different loan types
                if (row._isSystemOnly) {
                  // System-only loans (no OCR data) - light blue background
                  return {
                    backgroundColor: '#e6f3ff',
                    opacity: 0.8,
                    borderLeft: '3px solid #007bff'
                  };
                } else if (row._hasData === false) {
                  // Loans with data but no scans - gray background
                  return {
                    backgroundColor: '#f5f5f5',
                    opacity: 0.7
                  };
                }
                return {};
              }}
            />
            
            {totalPages > 1 && (
              <PaginationContainer>
                <PaginationInfo>
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredData.length)} of {filteredData.length} cases
                </PaginationInfo>
                
                <PaginationControls>
                  <PaginationButton
                    onClick={() => loadPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </PaginationButton>
                  
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <PaginationButton
                        key={pageNum}
                        onClick={() => loadPage(pageNum)}
                        className={currentPage === pageNum ? 'active' : ''}
                      >
                        {pageNum}
                      </PaginationButton>
                    );
                  })}
                  
                  <PaginationButton
                    onClick={() => loadPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </>
        ) : (
          <NoDataMessage>
            No contracts found.
          </NoDataMessage>
        )}

      </div>
      
      <ColumnVisibilityModal
        isOpen={showColumnModal}
        onClose={() => setShowColumnModal(false)}
        columns={(() => {
          return records.length > 0 ? Object.keys(records[0]).filter(key => 
            key !== 'loan_id' && !key.endsWith('_match') && key !== 'actions'
          ).map(key => ({
            key,
            title: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
          })) : [];
        })()}
        visibleColumns={visibleColumns}
        onToggleColumn={(columnKey) => {
          setVisibleColumns(prev => ({
            ...prev,
            [columnKey]: !prev[columnKey]
          }));
        }}
        onSave={(newVisibleColumns) => {
          setVisibleColumns(newVisibleColumns);
          // Save to localStorage
          try {
            localStorage.setItem('caseListVisibleColumns', JSON.stringify(newVisibleColumns));
          } catch (error) {
            console.error('Failed to save column visibility:', error);
          }
        }}
      />
      
      {/* Comments Modal */}
      <CommentsModal
        isOpen={showCommentsModal}
        onClose={() => {
          setShowCommentsModal(false);
          setSelectedLoanForComments(null);
        }}
        loanId={selectedLoanForComments}
        loanData={selectedLoanForComments ? currentPageData.find(row => 
          (row['loan_id'] || row['Loan ID'] || row['Loan_ID']) === selectedLoanForComments
        ) : null}
      />
      
      {/* Field Modal */}
      {selectedField && (
        <FieldModal
          isOpen={showFieldModal}
          onClose={() => {
            setShowFieldModal(false);
            setSelectedField(null);
            setSelectedLoanForField(null);
          }}
          fieldName={selectedField.fieldName}
          currentValues={selectedField.currentValues}
          loanId={selectedField.loanId}
          borrowerName={selectedField.borrowerName}
          onSave={async (overrideData) => {
            try {
              console.log('Saving field overrides:', overrideData);
              
              // Save each override type that was selected
              const savePromises = overrideData.overrideTypes.map(async (overrideType) => {
                const response = await apiFetch('/overrides/', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    loan_id: selectedField.loanId,
                    field_name: selectedField.fieldName,
                    override_type: overrideType,
                    overridden_value: overrideData.overrideValues[overrideType],
                    comment: overrideData.comment,
                    created_by: overrideData.createdBy || 'Current User'
                  })
                });
                
                if (!response.ok) {
                  throw new Error(`Failed to save ${overrideType} override`);
                }
                
                return response.json();
              });
              
              await Promise.all(savePromises);
              
              // Show success notification
              dispatch(addNotification({
                type: 'success',
                message: '✅ Field overrides saved successfully!',
                duration: 3000
              }));
              
              // Close modal and refresh data
              setShowFieldModal(false);
              setSelectedField(null);
              setSelectedLoanForField(null);
              
              // Refresh the current page to show updated data
              loadPage(currentPage);
              
            } catch (error) {
              console.error('Error saving field overrides:', error);
              dispatch(addNotification({
                type: 'error',
                message: '❌ Failed to save field overrides. Please try again.',
                duration: 5000
              }));
            }
          }}
          loading={false}
          showDocumentResults={false}
          historyData={fieldHistory}
        />
      )}
    </PageContainer>
  );
}

export default DatabaseViewer;
